import { marked } from 'marked';
import { Article } from '@/types/article';

/**
 * Parse markdown content to HTML
 */
export async function parseMarkdown(markdown: string): Promise<string> {
  try {
    if (!markdown) {
      console.error('Empty markdown content received');
      return '<p>No content available</p>';
    }
    
    if (markdown === 'Article content unavailable') {
      console.error('Article content unavailable message received');
      return '<p>This article is currently unavailable</p>';
    }
    
    // Log first 100 chars of markdown for debugging
    console.log(`Parsing markdown content: ${markdown.substring(0, 100)}...`);
    
    // Use marked to convert markdown to HTML
    marked.setOptions({
      gfm: true,         // GitHub flavored markdown
      breaks: true,      // Render line breaks as <br>
      silent: false      // Show warnings and errors in console
    });
    
    const html = marked.parse(markdown);
    
    // Verify HTML content was generated
    if (!html || typeof html !== 'string') {
      console.error('Failed to generate HTML from markdown');
      return '<p>Error rendering article content</p>';
    }
    
    // Log success
    console.log(`Successfully parsed markdown to HTML, length: ${html.length}`);
    return html;
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return '<p>Error parsing article content</p>';
  }
}

/**
 * Calculate estimated reading time for an article
 */
export function calculateReadTime(content: string): number {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
}

/**
 * Format a date for display
 */
export const formatPublishDate = (dateString: string | null): string => {
  if (!dateString) return 'Draft';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

/**
 * Filter articles by category
 */
export function filterArticlesByCategory(articles: Article[], categorySlug: string): Article[] {
  return articles.filter(article => 
    article.categories.some(category => category.slug === categorySlug)
  );
}

/**
 * Filter articles by tag
 */
export function filterArticlesByTag(articles: Article[], tagSlug: string): Article[] {
  return articles.filter(article => 
    article.tags.some(tag => tag.slug === tagSlug)
  );
}

/**
 * Sort articles by date (newest first)
 */
export function sortArticlesByDate(articles: Article[]): Article[] {
  return [...articles].sort((a, b) => {
    // Handle null dates by treating them as oldest
    if (!a.publishedAt) return 1;
    if (!b.publishedAt) return -1;
    const dateA = new Date(a.publishedAt).getTime();
    const dateB = new Date(b.publishedAt).getTime();
    return dateB - dateA;
  });
}

/**
 * Get related articles based on categories and tags
 */
export function getRelatedArticles(article: Article, allArticles: Article[], count: number = 3): Article[] {
  // Filter out the current article
  const otherArticles = allArticles.filter(a => a.id !== article.id);
  
  // Calculate relevance score for each article
  const scoredArticles = otherArticles.map(a => {
    let score = 0;
    
    // Score for matching categories
    article.categories.forEach(category => {
      if (a.categories.some(c => c.id === category.id)) {
        score += 2;
      }
    });
    
    // Score for matching tags
    article.tags.forEach(tag => {
      if (a.tags.includes(tag)) {
        score += 1;
      }
    });
    
    return { article: a, score };
  });
  
  // Sort by relevance score (highest first) and then by date (newest first)
  return scoredArticles
    .sort((a, b) => {
      if (a.score !== b.score) {
        return b.score - a.score;
      }
      
      // Handle null dates by treating them as oldest
      if (!a.article.publishedAt) return 1;
      if (!b.article.publishedAt) return -1;
      const dateA = new Date(a.article.publishedAt).getTime();
      const dateB = new Date(b.article.publishedAt).getTime();
      return dateB - dateA;
    })
    .slice(0, count)
    .map(item => item.article);
}

/**
 * Create an excerpt from content if one isn't provided
 */
export function createExcerpt(content: string, maxLength: number = 160): string {
  // Strip HTML tags
  const strippedContent = content.replace(/<[^>]*>/g, '');
  
  if (strippedContent.length <= maxLength) {
    return strippedContent;
  }
  
  let excerpt = strippedContent.substring(0, maxLength);
  
  // Try to find the last space to avoid cutting words
  const lastSpace = excerpt.lastIndexOf(' ');
  if (lastSpace > 0) {
    excerpt = excerpt.substring(0, lastSpace);
  }
  
  return excerpt + '...';
}

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}; 